import React from 'react';
import { createPortal } from 'react-dom';
import { Row, Col } from 'reactstrap';
import { debounce } from 'lodash-es';
import { APIProvider, Map } from '@vis.gl/react-google-maps';
import { faAddressCard, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { build } from '@s20/common/util/url';
import { httpClient } from '@s20/services/httpClient';
import { PanTo } from '../common/Map/usePanTo.hook';
import CommonContext, { ApiRoutes } from '../Common';
import { onReactSelectChanged, FormCheckbox } from '../common/forms/FormElements';
import SlideForm from '../common/forms/SlideForm';
import { CountyCaptureType } from '../tenant/Tenant';
import { Address } from './Address';
import AddressInput from './AddressInput';

const isCompleteEnoughToGeocode = (address) => address && address.address1 && address.city && address.state;

const AddressPinMap = ({
    apiKey, mapId, center, onDragEnd,
}) => {
    if (!center || !apiKey) return null;
    return (
        <div
            className="position-relative mt-2"
            style={{ height: '220px' }}
        >
            <APIProvider apiKey={apiKey}>
                <Map
                    style={{ width: '100%', height: '100%' }}
                    defaultCenter={center}
                    defaultZoom={16}
                    gestureHandling="greedy"
                    mapTypeControl={false}
                    streetViewControl={false}
                    fullscreenControl={false}
                    zoomControl={false}
                    scaleControl={false}
                    clickableIcons={false}
                    cameraControl={false}
                    mapId={mapId || null}
                    onDragend={(e) => {
                        const c = e.map.getCenter();
                        onDragEnd({ lat: c.lat(), lng: c.lng() });
                    }}
                >
                    <PanTo target={center} />
                </Map>
            </APIProvider>
            <div className="map-pin">
                <FontAwesomeIcon
                    icon={faLocationDot}
                    className="text-danger"
                    size="2x"
                />
            </div>
        </div>
    );
};

export default class AddressFormNew extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            address: new Address(props.countryId, props.countryCode, props.timeZone),
            formTitle: 'New Address',
            formValidated: false,
            loading: true,
            mapPanTarget: null,
            errors: {},
            validationMessage: '',
            show: false,
            showSameAs: false,
            showSameAsTitle: '',
            usStateObjects: [],
            usCounties: props.usCounties || [],
            isCountyCaptureEnabled: false,
            saveDisabled: false,
            otherFieldName: null,
            validateOnOpen: false,
        };

        this.onSubmit = this.onSubmit.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onSelectChange = this.onSelectChange.bind(this);
        this.onUsStateChange = this.onUsStateChange.bind(this);
        this.debouncedGeoLookup = debounce(this.performGeoLookup, 1000);
    }

    componentDidMount() {
        return this.populateState();
    }

    componentDidUpdate(prevProps) {
        // Check if countryId has changed
        if (prevProps.countryId !== this.props.countryId) {
            this.setState((state) => ({
                address: {
                    ...state.address,
                    countryId: this.props.countryId,
                    countryCode: this.props.countryCode,
                },
            }));

            // The US state list is fetched per countryId; when a caller mounts this form
            // before it knows the countryId (e.g. seeding it later via a ref), the list
            // fetched at mount time is stale/empty and must be re-fetched here.
            void this.populateState();
        }
    }

    onChange = async (e, setFieldValue) => {
        if (!e || !e.target || !e.target.name) {
            return;
        }

        const changedPropertyName = e.target.name.split('.').pop();

        const { address } = this.state;

        address[changedPropertyName] = e.target.value;

        if (isCompleteEnoughToGeocode(address) && ['address1', 'city', 'state', 'country'].includes(changedPropertyName)) {
            this.setState({ address, saveDisabled: true });
            await this.debouncedGeoLookup(setFieldValue);
        } else {
            this.setState({ address });
        }
    };

    onCheckedChanged = (e) => {
        const { address } = this.state;
        address[e.target.name] = e.target.checked;
        this.setState({ address });
    };

    onClose = () => {
        this.resetForm();
        this.context.setFormOpened(false);
        this.setState({ show: false });
    };

    onSelectChange = onReactSelectChanged;

    onSubmit = () => {
        this.props.onSaveCallback({ ...this.state.address, otherFieldName: this.state.otherFieldName });
        this.onClose();
    };

    onUsStateChange = async (item, setFieldValue) => {
        const { address } = this.state;

        address.state = item?.value ?? '';
        address.usStateId = item?.id;

        if (setFieldValue) {
            const namePrefix = this.props.namePrefix ?? 'address';
            const prefixName = namePrefix ? `${namePrefix}.` : '';

            setFieldValue(`${prefixName}state`, address.state);
            setFieldValue(`${prefixName}usStateId`, address.usStateId);
        }

        if (isCompleteEnoughToGeocode(address)) {
            this.setState({ address, saveDisabled: true });
            await this.debouncedGeoLookup(setFieldValue);
        } else {
            this.setState({ address });
        }
    };

    getIndicatedCountyId = (mapResponse) => {
        if (
            !mapResponse ||
            mapResponse.status !== 'OK' ||
            !mapResponse.results ||
            mapResponse.results.length === 0
        ) {
            return null;
        }

        const { address, usCounties, usStateObjects } = this.state;

        // Find the active state based on the selected address's state
        const activeState = usStateObjects.find(
            (s) => s.value === address.state || s.abbreviation === address.state,
        );

        if (!activeState) {
            return null;
        }

        // Filter counties for the active state
        const activeCounties = usCounties.filter(
            (c) => c.usStateId === activeState.id,
        );

        // Extract "administrative_area_level_2" (County) from the Google Maps response
        const responseCounty = mapResponse.results[0].address_components.find(
            (ac) => ac.types.includes('administrative_area_level_2'),
        );

        if (responseCounty) {
            // Match the parent county
            const activeCounty = activeCounties.find(
                (c) =>
                    c.name === responseCounty.long_name || // Exact match
                    c.name === responseCounty.short_name || // Short match
                    c.name.startsWith(`${responseCounty.long_name} (Independent City)`), // Match independent cities
            );

            if (activeCounty) {
                // Dynamically find sub-counties based on parentCountyId
                const subCounties = usCounties.filter(
                    (c) => c.parentCounty?.id === activeCounty.id,
                );

                if (subCounties.length > 0) {
                    // Match sub-county by city
                    const subCounty = subCounties.find((sub) =>
                        sub.cities?.some(
                            (city) =>
                                city.toLowerCase().trim() ===
                                address.city.toLowerCase().trim(),
                        ),
                    );

                    if (subCounty) {
                        // Preserve both parent and sub-county details
                        address.subCountyName = subCounty.name;
                        address.parentCountyName = activeCounty.name; // Store parent name for display
                        return subCounty.id; // Return sub-county ID for saving
                    }
                }

                // If no sub-county is matched, fall back to parent county
                address.parentCountyName = activeCounty.name;
                address.subCountyName = null; // Clear sub-county name
                return activeCounty.id;
            }
        }

        // Fallback to using "locality" (City) if no "administrative_area_level_2" is found
        const responseLocality = mapResponse.results[0].address_components.find(
            (ac) => ac.types.includes('locality'),
        );

        if (responseLocality) {
            const activeCounty = activeCounties.find(
                (c) =>
                    c.name === responseLocality.long_name || // Exact match
                    c.name === responseLocality.short_name || // Short match
                    c.name.startsWith(`${responseLocality.long_name} (Independent City)`), // Match independent cities
            );

            if (activeCounty) {
                address.parentCountyName = activeCounty.name;
                address.subCountyName = null;
                return activeCounty.id;
            }
        }

        return null;
    };

    open = (address, title, showSameAs, showSameAsTitle, otherFieldName, options = {}) => {
        this.resetForm();
        const currentAddress = { ...address };
        this.context.setFormOpened(true);

        const { validateOnOpen } = options || {};

        const countyCaptureEnabled = this.isCountyCaptureEnabled();

        // The checkbox is a per-edit choice, never carried over from a previous address.
        currentAddress.duplicate = false;

        this.setState({
            address: currentAddress,
            isCountyCaptureEnabled: countyCaptureEnabled,
            show: true,
            formTitle: title ?? (address.id ? 'Edit Address' : 'New Address'),
            showSameAs,
            showSameAsTitle,
            otherFieldName,
            validateOnOpen: validateOnOpen || false,
            mapPanTarget: (address.latitude && address.longitude)
                ? { lat: address.latitude, lng: address.longitude }
                : null,
        });
    };

    populateState = async () => {
        const usStateObjects = this.props.countryId
            ? await httpClient.js(ApiRoutes.typeAheads.USStatesbyCountryId(this.props.countryId))
            : [];

        const countyCaptureEnabled = this.isCountyCaptureEnabled();

        this.setState({
            usCounties: this.props.usCounties?.length > 0 ? this.props.usCounties : await httpClient.js(ApiRoutes.USCounties.all()),
            usStateObjects,
            tenantSettings: this.context.tenant.tenantSettings,
            isCountyCaptureEnabled: countyCaptureEnabled,
            loading: false,
        });
    };

    resetForm = () => this.setState({ formValidated: false });

    performGeoLookup = async (setFieldValue) => {
        const {
            address, tenantSettings, usCounties,
        } = this.state;

        if (!isCompleteEnoughToGeocode(address)) {
            this.setState({ saveDisabled: false });
            return;
        }

        const apiKey = tenantSettings.googleMapsApiKey;

        const url = build('https://maps.googleapis.com/maps/api/geocode/json', {
            address: `${address.address1} ${address.city},${address.state} ${address.countryCode}`,
            components: `country:${address.countryCode}`,
            result_type: 'administrative_area_level_2',
            key: apiKey,
        });

        const geoRes = await fetch(url);

        const geoData = await geoRes.json();
        const geo = geoData?.results?.[0];

        if (!geo) {
            address.timeZone = this.props.timeZone || tenantSettings.timeZone;
            this.setState({ address, saveDisabled: false });
            return;
        }

        const { lat, lng } = geo.geometry.location;
        address.latitude = lat;
        address.longitude = lng;

        const countyId = await this.getCountyId(geoData, address, lat, lng, tenantSettings);

        if (this.isCountyCaptureEnabled()) {
            address.usCountyId = countyId;
        }

        const county = usCounties.find((c) => c.id === countyId);

        if (countyId && county?.timeZoneId) {
            address.timeZone = county.timeZoneId;
        } else {
            const timestamp = Math.floor(Date.now() / 1000);

            const tzUrl = build('https://maps.googleapis.com/maps/api/timezone/json', {
                timestamp,
                location: `${lat},${lng}`,
                key: apiKey,
            });

            const tzRes = await fetch(tzUrl);

            const tzData = await tzRes.json();

            if (tzData?.timeZoneId) {
                if (countyId) {
                    await httpClient.post(
                        ApiRoutes.USCounties.addTimeZone(countyId),
                        {
                            timeZoneId: tzData.timeZoneId,
                        },
                    );
                }

                address.timeZone = tzData.timeZoneId;
            } else {
                address.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            }
        }

        if (!address.timeZone) {
            address.timeZone = this.props.timeZone || tenantSettings.timeZone;
        }

        // Left here intentionally for production diagnostic purposes
        console.info('Found timezone:', address.timeZone);

        if (setFieldValue) {
            const namePrefix = this.props.namePrefix ?? 'address';
            const prefixName = namePrefix ? `${namePrefix}.` : '';

            setFieldValue(`${prefixName}usCountyId`, countyId);
            setFieldValue(`${prefixName}timeZone`, address.timeZone);
            setFieldValue(`${prefixName}latitude`, lat);
            setFieldValue(`${prefixName}longitude`, lng);
        }
        this.setState({ address, saveDisabled: false, mapPanTarget: { lat, lng } });
    };

    onPinMoved = async ({ lat, lng }, setFieldValue) => {
        const { address, tenantSettings } = this.state;
        const url = build('https://maps.googleapis.com/maps/api/geocode/json', {
            latlng: `${lat},${lng}`,
            key: tenantSettings.googleMapsApiKey,
        });
        const res = await fetch(url);
        const data = await res.json();
        const result = data?.results?.[0];
        const get = (types, short = false) => {
            const c = result?.address_components?.find((x) => x.types.some((t) => types.includes(t)));
            return short ? c?.short_name : c?.long_name;
        };
        const streetNumber = get(['street_number']);
        const route = get(['route']);
        const city = get(['locality']) || get(['administrative_area_level_3']);
        const state = get(['administrative_area_level_1'], true);
        const zip = get(['postal_code']);
        const address1 = streetNumber && route
            ? `${streetNumber} ${route}`
            : (route ?? '');
        const namePrefix = this.props.namePrefix ?? 'address';
        const prefix = namePrefix ? `${namePrefix}.` : '';

        const countyId = await this.getCountyId(data, address, lat, lng, tenantSettings, { city, state });

        if (setFieldValue) {
            setFieldValue(`${prefix}usCountyId`, countyId);
            setFieldValue(`${prefix}address1`, address1);
            if (city) setFieldValue(`${prefix}city`, city);
            if (state) setFieldValue(`${prefix}state`, state);
            if (zip) setFieldValue(`${prefix}zip`, zip);
        }

        this.setState({
            address: {
                ...address,
                address1,
                ...(city ? { city } : {}),
                ...(state ? { state } : {}),
                ...(zip ? { zip } : {}),
                ...(this.isCountyCaptureEnabled() ? { usCountyId: countyId } : {}),
                latitude: lat,
                longitude: lng,
            },
            // mapPanTarget intentionally NOT updated — prevents PanMapTo from re-panning
        });
    };

    getCountyId = async (geoData, address, lat, lng, tenantSettings, fallbackInfo) => {
        let countyId = this.getIndicatedCountyId(geoData);
        const { city, state } = fallbackInfo || {};



        // Sometimes the first lookup doesn't find a county
        // This can be caused by the address being between county borders or the address not being mapped
        // If this happens, typically we can get the specific county by looking up the lat/lng
        if (!countyId) {
            const latLngUrl = build('https://maps.googleapis.com/maps/api/geocode/json', {
                latlng: `${lat},${lng}`,
                components: `country:${address.countryCode}`,
                result_type: 'administrative_area_level_2',
                key: tenantSettings.googleMapsApiKey,
            });
            const geoFromLatLngRes = await fetch(latLngUrl);
            const geoFromLatLngData = await geoFromLatLngRes.json();
            countyId = this.getIndicatedCountyId(geoFromLatLngData);
        }

        // Sometimes the second lookup doesn't find a county
        // This can sometimes happen with very rural addresses where the lat/lng is out in the middle of the woods
        // If this happens, we can fall back to looking up which county the city centre is in
        // This isn't as precise (some cities may fall in multiple counties) but these three approaches should always result in the correct county being selected
        if (!countyId && (city || address.city) && (state || address.state)) {
            const cityUrl = build('https://maps.googleapis.com/maps/api/geocode/json', {
                address: `${city || address.city},${state || address.state} ${address.countryCode}`,
                components: `country:${address.countryCode}`,
                result_type: 'administrative_area_level_2',
                key: tenantSettings.googleMapsApiKey,
            });
            const geoResFromCity = await fetch(cityUrl);
            const cityGeoData = await geoResFromCity.json();
            countyId = this.getIndicatedCountyId(cityGeoData);
        }

        return countyId;
    };

    isCountyCaptureEnabled = () => {
        const { tenantSettings } = this.context.tenant;
        return (tenantSettings.captureCounty == CountyCaptureType.AllTimesheets && Boolean(this.props.contractId))
            || (tenantSettings.captureCounty == CountyCaptureType.PrevailingWageTimesheets && this.props.contractUsesPrevailingWage)
            || (tenantSettings.captureCounty == CountyCaptureType.UnionTimesheets && this.props.isUnionWork);
    };

    renderForm = (setFieldValue) => {
        const id = this.props.id ?? 'addressForm';
        const namePrefix = this.props.namePrefix ?? 'address';

        if (this.state.validateOnOpen && isCompleteEnoughToGeocode(this.state.address)) {
            this.setState({ validateOnOpen: false });

            void this.performGeoLookup(setFieldValue);
        }

        const {
            address,
            usCounties,
            isCountyCaptureEnabled,
        } = this.state;

        return (
            <>
                <Row className="g-0">
                    <Col>
                        <AddressInput
                            namePrefix={namePrefix}
                            single
                            onChange={(e) => this.onChange(e, setFieldValue)}
                            address={address}
                            isCountyCaptureEnabled={isCountyCaptureEnabled}
                            usCounties={usCounties}
                            onStateChanged={(e) => this.onUsStateChange(e, setFieldValue)}
                            workTypeId={this.props.workTypeId}
                        />

                        {!!this.state.showSameAs && (
                            <FormCheckbox
                                small
                                className="ps-0 mt-2"
                                id={`duplicate-${id}`}
                                name="duplicate"
                                checked={address.duplicate}
                                onChange={this.onCheckedChanged}
                                labelText={this.state.showSameAsTitle ?? ''}
                            />
                        )}
                    </Col>
                </Row>

                {this.state.mapPanTarget && (
                    <AddressPinMap
                        apiKey={this.state.tenantSettings?.googleMapsApiKey}
                        mapId={this.state.tenantSettings?.mapId}
                        center={this.state.mapPanTarget}
                        onDragEnd={(location) => this.onPinMoved(location, setFieldValue)}
                    />
                )}
            </>
        );
    };

    render() {
        const {
            address,
            errors,
            formValidated,
            validationMessage,
            saveDisabled,
        } = this.state;

        if (!address) {
            return null;
        }

        const id = this.props.id ?? 'addressForm';

        const { setFieldValue } = this.props;

        if (setFieldValue) {
            return this.renderForm(setFieldValue);
        }

        const namePrefix = this.props.namePrefix ?? 'address';
        const prefixName = namePrefix ? `${namePrefix}.` : '';

        // Namespace address keys to match input field names (e.g. address1 → address.address1)
        const namespacedInitialValues = address
            ? Object.fromEntries(Object.entries(address).map(([k, v]) => [`${prefixName}${k}`, v]))
            : {};

        return (
            createPortal(
                <SlideForm
                    loading={this.state.loading}
                    show={this.state.show}
                    id={id}
                    formIcon={faAddressCard}
                    formTitle={this.state.formTitle}
                    ref={this.formRef}
                    setIsValidated={(value) => this.setState({ formValidated: value })}
                    isValidated={formValidated}
                    onSubmit={this.onSubmit}
                    onClose={this.onClose}
                    onDelete={this.onDelete}
                    errors={errors}
                    onClearErrors={this.onClearErrors}
                    error={validationMessage}
                    saveDisabled={saveDisabled}
                    initialValues={namespacedInitialValues}
                >
                    {({ setFieldValue }) => this.renderForm(setFieldValue)}
                </SlideForm>,
                document.body,
            )
        );
    }
}